import { vec3 } from 'gl-matrix';
import { atom } from 'recoil';
import { DcmJsDatasetWrapper } from '~/domain/dicom/dcmJsWrapper';
import { recoilPersist } from 'recoil-persist';
import {
    ImageSlicePositions,
    PatientSpaceCoordsProps,
} from '~/containers/viewer/viewport/Viewport';
import { Orientations } from '~/engine/view/views/PlaneView';

export enum GridLayoutMode {
    Default = 'default',
    SideBySide = 'side-by-side',
}

const { persistAtom } = recoilPersist({
    key: 'viewerPersist',
    storage: localStorage,
    converter: JSON,
});

// REMOVE IN FUTURE: This is just a flag for export modal rendering purposes...
export const exportableItemsCountState = atom<number>({
    key: 'exportableItemsCountState',
    default: 0,
});

export const showFullscreenState = atom<boolean>({
    key: 'showFullscreenState',
    default: false,
});

export const busyIndicatorState = atom<{
    isOpen: boolean;
    message?: string;
}>({
    key: 'busyIndicatorState',
    default: {
        isOpen: false,
    },
});

export const showSensitiveInfoState = atom<boolean>({
    // This atom is used to control the visibility of sensitive patient information e.g. patient name, patient ID, etc.
    key: 'showSensitiveInfoState',
    default: true,
});

// Controls visibility of the DICOM Sources modal (Search/Select/Finish)
export const dicomSourcesModalOpenState = atom<boolean>({
    key: 'dicomSourcesModalOpenState',
    default: false,
});

export const autoLoadResultsState = atom<boolean>({
    key: 'autoLoadResultsState',
    default: true,
});

// Three states: null for disabled DVH view, true for shown DVH view, false for hidden DVH view
export const showDVHViewComponentState = atom<boolean | null>({
    key: 'showDVHViewComponentState',
    default: null,
});

export const showDVHViewComponentFullScreenState = atom<boolean>({
    key: 'showDVHViewComponentFullScreenState',
    default: false,
});

export const showDoseStatisticsTableState = atom<boolean | null>({
    key: 'showDoseStatisticsTableState',
    default: null,
});

export const expandDoseStatisticsTableState = atom<boolean | null>({
    key: 'expandDoseStatisticsTableState',
    default: null,
});

export const showDoseStatisticsTableFullScreenState = atom<boolean | null>({
    key: 'showDoseStatisticsTableFullScreenState',
    default: null,
});

export enum WorkflowTypes {
    WORKSPACE,
    CONTOUR,
    DOSE,
    IMAGE,
    ADAPT,
    DATA_UPLOAD,
    SEG_VERIFY,
    REGISTER_IMAGES,
    COMPOSITE_CBCT,
}

export const workflowModalState = atom<{
    open: boolean;
    page: WorkflowTypes;
}>({
    key: 'workflowModalState',
    default: {
        open: false,
        page: WorkflowTypes.CONTOUR,
    },
});

export const viewportOrientationOrderState = atom<Orientations[]>({
    key: 'viewportOrientationOrderState',
    default: [
        Orientations.TRANSVERSAL_ORIENTATION,
        Orientations.SAGITTAL_ORIENTATION,
        Orientations.CORONAL_ORIENTATION,
    ],
});

export const coordinateDisplayExpandedState = atom<Orientations[] | null>({
    key: 'coordinateDisplayExpandedState',
    default: [
        Orientations.TRANSVERSAL_ORIENTATION,
        Orientations.SAGITTAL_ORIENTATION,
        Orientations.CORONAL_ORIENTATION,
    ],
});

export const imageValueAtCursorState = atom<number | null>({
    key: 'imageValueAtCursorState',
    default: null,
});

export const secondaryImageValueAtCursorState = atom<number | null>({
    key: 'secondaryImageValueAtCursorState',
    default: null,
});

export const doseIntensityValueAtCursorState = atom<number | null>({
    key: 'doseIntensityValueAtCursorState',
    default: null,
});

export const patientSpaceCoordsState = atom<PatientSpaceCoordsProps>({
    key: 'patientSpaceCoordsState',
    default: {
        coords: null,
        canvasCoords: null,
        orthoCoords: null,
    },
});

export const imageSlicesCountState = atom<number>({
    key: 'imageSlicesCountState',
    default: 0,
});

export const currentImageSliceOrientationPositionsState =
    atom<ImageSlicePositions>({
        key: 'currentImageSliceOrientationPositionsState',
        default: {
            TRANSVERSAL_ORIENTATION: '0',
            SAGITTAL_ORIENTATION: '0',
            CORONAL_ORIENTATION: '0',
        },
    });

export const sideBySideImageSliceOrientationPositionsState =
    atom<ImageSlicePositions>({
        key: 'sideBySideImageSliceOrientationPositionsState',
        default: {
            TRANSVERSAL_ORIENTATION: '0',
            SAGITTAL_ORIENTATION: '0',
            CORONAL_ORIENTATION: '0',
        },
    });

export enum ExportTypes {
    STRUCTURES,
    DOSE,
}

export const exportDatasetsModalOpenState = atom<boolean>({
    key: 'exportDatasetsModalOpenState',
    default: false,
});

export const currenPositionAutoScrollState = atom<vec3 | null>({
    key: 'currenPositionAutoScrollState',
    default: null,
});

export const isAutoScrollingState = atom<boolean>({
    key: 'isAutoScrollingState',
    default: false,
});

export const pseudonymizeState = atom({
    key: 'pseudonymizeState',
    default: true,
});

export const enableExportMonacoDirFileState = atom<boolean>({
    key: 'enableExportMonacoDirFileState',
    default: false,
    effects: [persistAtom],
});

export const gridLayoutModeState = atom<GridLayoutMode>({
    key: 'gridLayoutModeState',
    default: GridLayoutMode.Default,
});

export const areEnvironmentsLockedState = atom<boolean>({
    key: 'areEnvironmentsLockedState',
    default: false,
});

export const lastMousePositionState = atom<{
    clientX: number;
    clientY: number;
} | null>({
    key: 'lastMousePositionState',
    default: null,
});

export const dicomViewerAtom = atom<{
    dataset: DcmJsDatasetWrapper;
    title: string;
} | null>({
    key: 'dicomViewerAtom',
    default: null,
    dangerouslyAllowMutability: true,
});
